import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import api from '../lib/api';

interface AppSettings {
  shopify_store_uae: string;
  tiktok_shop_de: string;
  dhl_account: string;
  deepseek_model: string;
  temperature: number;
  video_render_quality: string;
  auto_publish_products: boolean;
  auto_fulfill_orders: boolean;
  auto_optimize_ads: boolean;
  default_market: string;
  block_non_compliant: string;
}

export default function Settings() {
  const queryClient = useQueryClient();
  const [config, setConfig] = useState<AppSettings | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Fetch Settings
  const { data: serverSettings, isLoading, isError } = useQuery<AppSettings>({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await api.get('/settings');
      return response.data;
    },
  });

  // Sync state when data loads
  useEffect(() => {
    if (serverSettings) {
      setConfig(serverSettings);
    }
  }, [serverSettings]);

  // Save Settings Mutation
  const saveMutation = useMutation({
    mutationFn: async (updatedConfig: AppSettings) => {
      const response = await api.post('/settings', { config: updatedConfig });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    },
  });

  const handleChange = (key: keyof AppSettings, value: any) => {
    if (config) {
      setConfig({ ...config, [key]: value });
      setSaveSuccess(false);
    }
  };

  const handleSave = () => {
    if (config) {
      saveMutation.mutate(config);
    }
  };

  if (isLoading || !config) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <Loader2 className="h-8 w-8 text-primary-500 animate-spin" />
        <p className="text-gray-400">Loading settings...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4 text-red-500">
        <AlertCircle className="h-8 w-8" />
        <p>Failed to load settings. Please check if the API is running.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Settings</h1>
          <p className="text-gray-400 mt-1">Configure your AutoPilot agent</p>
        </div>
        
        {saveSuccess && (
          <div className="flex items-center text-green-500 bg-green-500/10 px-4 py-2 rounded-lg border border-green-500/20">
            <CheckCircle className="h-5 w-5 mr-2" />
            <span className="text-sm font-medium">Settings saved successfully</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Platform Settings */}
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">Platform Integrations</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Shopify Store (UAE)</label>
              <input 
                type="text" 
                className="input w-full" 
                placeholder="your-store.myshopify.com"
                value={config.shopify_store_uae}
                onChange={(e) => handleChange('shopify_store_uae', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">TikTok Shop (Germany)</label>
              <input 
                type="text" 
                className="input w-full" 
                placeholder="Shop ID"
                value={config.tiktok_shop_de}
                onChange={(e) => handleChange('tiktok_shop_de', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">DHL Account</label>
              <input 
                type="text" 
                className="input w-full" 
                placeholder="Account Number"
                value={config.dhl_account}
                onChange={(e) => handleChange('dhl_account', e.target.value)}
              />
            </div>
          </div>
          <button 
            onClick={handleSave} 
            disabled={saveMutation.isPending}
            className="btn-primary mt-4 flex items-center disabled:opacity-50"
          >
            {saveMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Save Changes
          </button>
        </div>

        {/* AI Settings */}
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">AI Configuration</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">DeepSeek Model</label>
              <select 
                className="input w-full" 
                value={config.deepseek_model}
                onChange={(e) => handleChange('deepseek_model', e.target.value)}
              >
                <option value="deepseek-chat">DeepSeek Chat</option>
                <option value="deepseek-coder">DeepSeek Coder</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Temperature ({config.temperature})</label>
              <input 
                type="range" 
                min="0" 
                max="1" 
                step="0.1"
                value={config.temperature}
                onChange={(e) => handleChange('temperature', parseFloat(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Precise</span>
                <span>Creative</span>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Video Render Quality</label>
              <select 
                className="input w-full" 
                value={config.video_render_quality}
                onChange={(e) => handleChange('video_render_quality', e.target.value)}
              >
                <option value="high">High (1080p)</option>
                <option value="medium">Medium (720p)</option>
                <option value="fast">Fast (480p)</option>
              </select>
            </div>
          </div>
          <button 
            onClick={handleSave} 
            disabled={saveMutation.isPending}
            className="btn-primary mt-4 flex items-center disabled:opacity-50"
          >
            {saveMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Save Changes
          </button>
        </div>

        {/* Automation Settings */}
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">Automation Rules</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium">Auto-publish Products</p>
                <p className="text-sm text-gray-400">Publish immediately after compliance check</p>
              </div>
              <label className="relative cursor-pointer">
                <input 
                  type="checkbox" 
                  className="sr-only peer" 
                  checked={config.auto_publish_products}
                  onChange={(e) => {
                    handleChange('auto_publish_products', e.target.checked);
                    saveMutation.mutate({ ...config, auto_publish_products: e.target.checked });
                  }}
                />
                <div className="w-11 h-6 bg-dark-700 rounded-full peer peer-checked:bg-primary-600 transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
              </label>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium">Auto-fulfill Orders</p>
                <p className="text-sm text-gray-400">Automatically create shipping labels</p>
              </div>
              <label className="relative cursor-pointer">
                <input 
                  type="checkbox" 
                  className="sr-only peer" 
                  checked={config.auto_fulfill_orders}
                  onChange={(e) => {
                    handleChange('auto_fulfill_orders', e.target.checked);
                    saveMutation.mutate({ ...config, auto_fulfill_orders: e.target.checked });
                  }}
                />
                <div className="w-11 h-6 bg-dark-700 rounded-full peer peer-checked:bg-primary-600 transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
              </label>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium">Auto-optimize Ads</p>
                <p className="text-sm text-gray-400">Adjust budgets based on ROAS</p>
              </div>
              <label className="relative cursor-pointer">
                <input 
                  type="checkbox" 
                  className="sr-only peer" 
                  checked={config.auto_optimize_ads}
                  onChange={(e) => {
                    handleChange('auto_optimize_ads', e.target.checked);
                    saveMutation.mutate({ ...config, auto_optimize_ads: e.target.checked });
                  }}
                />
                <div className="w-11 h-6 bg-dark-700 rounded-full peer peer-checked:bg-primary-600 transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
              </label>
            </div>
          </div>
        </div>

        {/* Compliance Settings */}
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">Compliance</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Default Market</label>
              <select 
                className="input w-full" 
                value={config.default_market}
                onChange={(e) => handleChange('default_market', e.target.value)}
              >
                <option value="DE">Germany (DE)</option>
                <option value="AE">UAE (AE)</option>
                <option value="SA">Saudi Arabia (SA)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Block Non-compliant Products</label>
              <select 
                className="input w-full" 
                value={config.block_non_compliant}
                onChange={(e) => handleChange('block_non_compliant', e.target.value)}
              >
                <option value="true">Yes - Block listing</option>
                <option value="false">No - Allow with warning</option>
              </select>
            </div>
            <div className="p-4 bg-dark-900 rounded-lg">
              <p className="text-sm text-gray-400 mb-2">Required Certifications</p>
              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">CE</span>
                <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">EPR</span>
                <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">GCC</span>
                <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded text-xs">SABER (pending)</span>
              </div>
            </div>
          </div>
          <button 
            onClick={handleSave} 
            disabled={saveMutation.isPending}
            className="btn-primary mt-4 flex items-center disabled:opacity-50"
          >
            {saveMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}
